import { Visibility } from "@material-ui/icons";
import { format } from "date-fns";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { CloseButton, FloatingLabel, Form, InputGroup } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { useGetDivisionListQuery, useLazyGetSelectedOrderDetailsQuery } from "../../../features/Dispatch/DispatchSlice";
import { useGetAllOrdersQuery } from '../../../features/order/orderSlice';
import EliteTable from "../../designSystem/EliteTable";
import OrderDetailsMin from "../../Dispatch/SamedayDispatch/OrderDetails/OrderDetailsMin";
import Wrapper from "../../Wrapper";
import { toast } from "react-toastify";
import Button from "../../designSystem/Button/Button";

const dateFields = [
    { label: "Ready Date", value: "ReadyDate" },
    { label: "Order Booking Date", value: "OrderBookingDate" },
    { label: "Order Picked Up Date", value: "OrderPickedupDate" },
    { label: "Order Delivered Date", value: "OrderDeliveredDate" },
];

const OrderTracing = () => {
    const { register, handleSubmit, reset, formState: { errors } } = useForm();
    const [filterVisibility, setFilterVisibility] = useState({});
    const { data: divisionList } = useGetDivisionListQuery();
    const [searchParams, setSearchParams] = useSearchParams();
    const [getDetails] = useLazyGetSelectedOrderDetailsQuery();
    const [tabs, setTabs] = useState([]);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [divisionId, setDivisionId] = useState(null);
    const [filters, setFilters] = useState({});
    const [gridPage, setGridPage] = useState(0);
    const [apiPage, setApiPage] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const initialSearchQuery = searchParams.get('search') || "";
    const [searchValue, setSearchValue] = useState(initialSearchQuery);
    const globalSearch = useRef("");
    const searchTimeout = useRef(null);
    const pageSize = 20;
    const { data: orders, isLoading, isFetching, error: getAllOrdersError } = useGetAllOrdersQuery({
        filters,
        pageNumber: apiPage,
        pageSize,
        divisionId,
    });
    const orderID = searchParams.get('orderID');
    const divID = searchParams.get('divID');

    useEffect(() => {
        if (initialSearchQuery) {
            setFilters(prev => ({
                ...prev,
                globalSearch: initialSearchQuery,
            }));
        }
    }, [initialSearchQuery]);

    useEffect(() => {
        if (selectedOrder == null) {
            setTabs([])
        }
    }, [selectedOrder])

    useEffect(() => {
        if (getAllOrdersError) {
            toast.error(`${getAllOrdersError?.data?.message}`, {
                position: "top-center",
            })
        }
    }, [getAllOrdersError])

    useEffect(() => {
        if (orderID && divID) {
            getDetails({ divisionId: divID, orderId: orderID }).then((result) => {
                if (result.data?.statusCode === 200) {
                    const orderData = result.data?.data || {};
                    setSelectedOrder(orderData);
                    handleAddOrderDetailsTab(orderData);
                } else {
                    console.error("Failed to fetch order details:", result.error);
                }
            }).catch((error) => {
                console.error("Error fetching order details:", error);
            });
        }
    }, [orderID, divID, getDetails]);

    useEffect(() => {
        if (orders?.data) {
            setTotalRecords(Number(orders?.data.totalRecords));
            setGridPage(Number(orders?.data.currentPageNumber) - 1);
            setApiPage(Number(orders?.data.currentPageNumber));
        }
    }, [orders]);

    const handlePageChange = (newPage) => {
        setGridPage(newPage);
        setApiPage(newPage + 1);
    };

    const handleGlobalSearch = (e) => {
        const value = e.target.value;
        setSearchValue(value);
        globalSearch.current = value;
        if (searchTimeout.current) {
            clearTimeout(searchTimeout.current);
        }
        if (value === "") {
            clearUrl(setSearchParams);
        }
        searchTimeout.current = setTimeout(() => {
            setFilters((prev) => ({
                ...prev,
                globalSearch: globalSearch.current,
            }));
        }, 300);
    };

    const handleFilterChange = (field, value) => {
        const updatedFilters = {
            ...filters,
            [field]: value,
        };
        setFilters(updatedFilters);
    };

    const handleSelectDivision = (e) => { setDivisionId(e.target.value) };
    const handleAddOrderDetailsTab = (order) => {
        setTabs((prevTabs) => {
            const isDuplicate = prevTabs.some((tab) => tab.orderId === order.orderId);
            if (isDuplicate || prevTabs.length >= 5) {
                return [...prevTabs.slice(0, -1), order];
            }

            return [...prevTabs, order];
        });
    };

    const onFilterDate = handleSubmit(({ dateField, fromDate, toDate }) => {
        setFilters((prev) => ({
            ...prev,
            dateField,
            fromDate: format(new Date(fromDate), "yyyy-MM-dd HH:mm"),
            toDate: format(new Date(toDate), "yyyy-MM-dd HH:mm"),
        }));
    })
    const handleCloseTab = (e, orderDetail) => {
        e.stopPropagation();
        setTabs((prevTabs) => {
            const newTabs = prevTabs.filter((tab) => tab.orderId !== orderDetail.orderId);
            if (orderDetail.orderId === selectedOrder?.orderId) {
                const indexOfClosedTab = prevTabs.findIndex((tab) => tab.orderId === orderDetail.orderId);
                const nextSelectedTab = newTabs[indexOfClosedTab - 1] || newTabs[indexOfClosedTab];
                setSelectedOrder(nextSelectedTab || null);
            }

            return newTabs;
        });
    }

    const clearFilters = () => {
        setFilters({});
        reset({
            dateField: "ReadyDate",
            fromDate: "",
            toDate: "",
        });
        setSearchValue("");
        globalSearch.current = "";
        setDivisionId('');
        setFilterVisibility({});
        clearUrl(setSearchParams);
    };

    const columns = useMemo(() => [
        {
            field: 'actions',
            headerName: 'Actions',
            width: 80,
            renderCell: (params) => {
                const onClick = async (e) => {
                    e.stopPropagation();
                    const result = await getDetails({ divisionId: params.row.divisionID, orderId: params.row.orderID });
                    if (result.data.statusCode === 200) {
                        setSelectedOrder(result.data?.data || {});
                        handleAddOrderDetailsTab({
                            ...result.data?.data || {},
                            divID: params.row.divisionID,
                        })
                    }
                };
                return (
                    <div className="action_btn">
                        <span className="view_btn" onClick={onClick}>
                            <Visibility />
                        </span>
                    </div>
                );
            },
        },
        { field: 'divisionName', headerName: 'Division', width: 120, hide: !(divisionId == null || divisionId === "") },
        { field: 'orderNumber', headerName: 'Order No.', width: 120 },
        { field: 'readyDate', headerName: 'Ready Date', width: 200 },
        { field: 'accountNo', headerName: 'Account No.', width: 120 },
        { field: 'orderStatus', headerName: 'Order Status', width: 120 },
        { field: 'vehicle', headerName: 'Vehicle Type', width: 120 },
        { field: 'service', headerName: 'Service Type', width: 120 },
        { field: 'consignorName', headerName: 'Consignor Name', width: 120 },
        { field: 'pickupContact', headerName: 'Pickup Contact', width: 120 },
        { field: 'pickupPhone', headerName: 'Pickup Phone', width: 120 },
        { field: 'pickupEmail', headerName: 'Pickup Email', width: 120 },
        { field: 'pickupStreetNumber', headerName: 'Pickup Street No.', width: 120 },
        { field: 'pickupStreetName', headerName: 'Pickup Street Name', width: 120 },
        { field: 'pickupProvince', headerName: 'Pickup Province', width: 120 },
        { field: 'pickupCity', headerName: 'Pickup City', width: 120 },
        { field: 'pickupCountry', headerName: 'Pickup Country', width: 120 },
        { field: 'pickupPostalCode', headerName: 'Pickup PostalCode', width: 120 },
        { field: 'pickupDriver', headerName: 'Pickup Driver', width: 120 },
        { field: 'pickupReference', headerName: 'Pickup Ref.', width: 120 },
        { field: 'waybill', headerName: 'Waybill', width: 120 },
        { field: 'orderReference', headerName: 'Order Ref.', width: 120 },
        { field: 'consigneeName', headerName: 'Consignee Name', width: 120 },
        { field: 'dropoffEmail', headerName: 'Dropoff Email', width: 120 },
        { field: 'dropoffPhone', headerName: 'Dropoff Phone no.', width: 120 },
        { field: 'dropoffStreetNumber', headerName: 'Dropoff Street No.', width: 120 },
        { field: 'dropoffStreetName', headerName: 'Dropoff Street Name', width: 120 },
        { field: 'dropoffProvince', headerName: 'Dropoff Province', width: 120 },
        { field: 'dropoffCity', headerName: 'Dropoff City', width: 120 },
        { field: 'dropoffCountry', headerName: 'Dropoff Country', width: 120 },
        { field: 'dropoffPostalCode', headerName: 'Drop offPostalCode', width: 120 },
        { field: 'dropoffDriver', headerName: 'Dropoff Driver', width: 120 },
        { field: 'dropoffReference', headerName: 'Dropoff Reference', width: 120 },
        { field: 'lineHauler', headerName: 'Line Hauler', width: 120 },
        { field: 'orderBookingDate', headerName: 'Order Booking Date', width: 120 },
        { field: 'orderPickedupDate', headerName: 'Order Picked Up Date', width: 120 },
        { field: 'orderDeliveredDate', headerName: 'Order Delivered Date', width: 120 },
        { field: 'orderType', headerName: 'Order Type', width: 120 },
        {
            field: 'pxDock',
            headerName: 'PX',
            width: 120,
            editable: false,
        },
        {
            field: 'dxDock',
            headerName: 'DX',
            width: 120,
            editable: false,
        },
        { field: 'tripNo', headerName: 'Trip No.', width: 120 },
        { field: 'proNumber', headerName: 'Pro Number', width: 120 },
        { field: 'customerName', headerName: 'Customer Name', width: 120 },
        { field: 'dropoffContact', headerName: 'Dropoff Contact', width: 120 },
        { field: 'pickupUnitNo', headerName: 'Pickup Unit No.', width: 120 },
        { field: 'dropoffUnitNo', headerName: 'dropoff Unit No.', width: 120 },
        { field: 'pcs', headerName: 'Pcs', width: 120 },
        { field: 'pcsUnit', headerName: 'Pcs Unit', width: 120 },
        { field: 'weight', headerName: 'Weight', width: 120 },
        { field: 'weightUnit', headerName: 'Weight Unit', width: 120 },
    ], [getDetails, divisionId]);

    const rows = useMemo(() => {
        if (!orders?.data?.orders || !Array.isArray(orders.data?.orders)) return [];

        return orders.data.orders?.map((order) => ({
            id: order.orderID,
            ...order,
            readyDate: new Date(order.readyDate).toLocaleString(),
            orderBookingDate: new Date(order.orderBookingDate).toLocaleString(),
            orderPickedupDate: order.orderPickedupDate
                ? new Date(order.orderPickedupDate).toLocaleString()
                : 'N/A',
            orderDeliveredDate: order.orderDeliveredDate
                ? new Date(order.orderDeliveredDate).toLocaleString()
                : 'N/A',
            dxDock: order?.dxDock,
            pxDock: order?.pxDock,
        }));
    }, [orders]);

    const width = window.screen.width;
    const height = window.screen.height;

    return (
        <Wrapper>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h3>Order Tracing</h3>
                <Button
                    fullWidth={false}
                    variant="outline-danger"
                    onClick={() => window.open("/order/order-taking-by-telephone", "_blank", `width=${width},height=${height},noopener,noreferrer`)}
                >
                    Generate Order
                </Button>
            </div>
            <div className="table-back p-3 my-3">
                <div className="d-flex gap-2 mb-3" >
                    <InputGroup style={{ maxWidth: "400px" }}>
                        <InputGroup.Text style={{ backgroundColor: "#f6f6f6", border: 0 }}>
                            <span> <i className="fa fa-search" aria-hidden="true"></i></span>
                        </InputGroup.Text>
                        <Form.Control
                            type="text"
                            placeholder="Search"
                            value={searchValue}
                            onChange={handleGlobalSearch}
                            style={{
                                border: 0,
                                borderRadius: "0 4px 4px 0",
                                backgroundColor: "#f6f6f6",
                            }}
                        />
                    </InputGroup>
                    <div className="form-floating">
                        <Form.Group controlId="drivernumber">
                            <FloatingLabel controlId="floatingSelectGrid" label="Select division">
                                <Form.Select aria-label="Floating label select example" name="divisionId" value={divisionId} onChange={handleSelectDivision}>
                                    <option value="">Select</option>
                                    {divisionList?.data?.map((data, index) => (
                                        <option key={index} value={data?.divisionId} >{data?.divisionName}</option>
                                    ))}
                                </Form.Select>
                            </FloatingLabel>
                        </Form.Group>
                    </div>
                </div>
                <div className="d-flex flex-column flex-md-row gap-2 justify-content-between " >
                    <div className="d-flex gap-2 w-100 flex-wrap flex-grow-1">
                        <div className="form-floating flex-grow-1 flex-md-grow-0">
                            <Form.Group controlId="dateField">
                                <FloatingLabel controlId="floatingSelectGrid" label="Date Fields">
                                    <Form.Select aria-label="Floating label select example" name="dateField"  {...register("dateField", { required: "Date Field is required" })}>
                                        {dateFields.map((field, index) => (
                                            <option key={index} value={field.value}>
                                                {field.label}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </FloatingLabel>
                            </Form.Group>
                        </div>
                        <FloatingLabel controlId="fromDate" label="From Date" className="flex-grow-1 flex-md-grow-0">
                            <Form.Control
                                type="datetime-local"
                                {...register("fromDate", { required: "From Date is required" })}
                            />
                            {errors.fromDate && <p className="text-danger">{errors.fromDate.message}</p>}
                        </FloatingLabel>
                        <FloatingLabel controlId="toDate" label="To Date" className="flex-grow-1 flex-md-grow-0">
                            <Form.Control
                                type="datetime-local"
                                {...register("toDate", { required: "To Date is required" })}
                            />
                            {errors.toDate && <p className="text-danger">{errors.toDate.message}</p>}
                        </FloatingLabel>
                        <Button
                            fullWidth={false}
                            style={{ height: "48px" }}
                            variant="primary"
                            onClick={onFilterDate}>
                            Search By Date
                        </Button>
                    </div>
                    <Button
                        style={{ height: "48px", width: "120px" }}
                        variant="outline-primary"
                        onClick={clearFilters}>
                        Clear Filters
                    </Button>
                </div>
            </div>
            {selectedOrder && tabs.length > 0 ?
                <div className="rounded-pill d-flex justify-content-between align-items-center w-100 table-back p-2 mb-3">
                    <div className="d-flex gap-1">
                        {tabs.map((orderDetail) => {
                            return (
                                <div onClick={() => window.open(`/order/order-tracking?orderID=${orderDetail.orderId}&divID=${orderDetail.divID}`, '_blank')}
                                    key={orderDetail.orderId} style={{ backgroundColor: "#bf2527" }} className="d-flex align-items-center rounded-pill text-white p-1 gap-1">
                                    <div>Order number:</div>
                                    <span>{orderDetail.orderNumber}</span>
                                    <CloseButton variant="white" style={{ fontSize: "10px" }}
                                        onClick={(e) => handleCloseTab(e, orderDetail)}
                                    />
                                </div>
                            )
                        })}
                    </div>
                </div> : null}
            <div className="mb-3">
                {selectedOrder && <OrderDetailsMin selectedOrder={selectedOrder} setSelectedOrder={setSelectedOrder} divisionId={divisionId} shouldShowGrossPriceSection />}
            </div>
            <div className="table-back" style={{ height: 600, width: '100%' }}>
                <EliteTable
                    rows={rows}
                    columns={columns}
                    pageSize={pageSize}
                    page={gridPage}
                    rowCount={totalRecords}
                    onPageChange={handlePageChange}
                    paginationMode="server"
                    rowsPerPageOptions={[20]}
                    onFilterChange={handleFilterChange}
                    filters={filters}
                    loading={isLoading || isFetching}
                    filterVisibility={filterVisibility}
                    setFilterVisibility={setFilterVisibility}
                />
            </div>
        </Wrapper >
    );
};

export default OrderTracing;
function clearUrl(setSearchParams) {
    setSearchParams((prevParams) => {
        const newParams = new URLSearchParams(prevParams);
        newParams.delete("search");
        return newParams;
    });
}

